import { AnimatePresence, motion } from "framer-motion";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motionDurations } from "./motion";

export type ToastTone = "default" | "success" | "warning" | "danger";

export type ToastItem = {
  id: string;
  message: ReactNode;
  tone?: ToastTone;
};

type ToastContextValue = {
  toasts: ToastItem[];
  push: (message: ReactNode, tone?: ToastTone) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function newId(): string {
  return `toast-${Math.random().toString(36).slice(2, 10)}`;
}

export function ToastProvider({
  children,
  autoDismissMs = 4000,
}: {
  children: ReactNode;
  autoDismissMs?: number;
}) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: ReactNode, tone: ToastTone = "default") => {
      const id = newId();
      setToasts((prev) => [...prev, { id, message, tone }]);
      if (autoDismissMs > 0) {
        window.setTimeout(() => dismiss(id), autoDismissMs);
      }
      return id;
    },
    [autoDismissMs, dismiss],
  );

  const value = useMemo(
    () => ({ toasts, push, dismiss }),
    [toasts, push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastHost toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx;
}

export function ToastHost({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="ax-toast-host" aria-live="polite">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            className={[
              "ax-toast",
              t.tone && t.tone !== "default" ? `ax-toast--${t.tone}` : "",
            ]
              .filter(Boolean)
              .join(" ")}
            role="status"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{
              duration: motionDurations.fast / 1000,
              ease: "easeOut",
            }}
            onClick={() => onDismiss(t.id)}
          >
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
